import { useJobillaOrgId } from '@/hooks'
import { type ImageData, useMediaImageIndex, useMediaImageUpload } from '@jobilla/nexus-sdk'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useMemo } from 'react'
import type { MediaAsset } from '../../ui'
import { type SignedImageUploadVariables, useSignedImageUpload } from './useSignedImageUpload'

function mapImageDataToMediaAsset(asset: ImageData): MediaAsset {
	return {
		...asset,
		fileName: asset.fileName,
		humanReadableSize: asset.humanReadableSize,
		createdAt: new Date(asset.createdAt),
		collection: asset.collectionName,
		isReady: true,
	}
}

export function useAssetHubData() {
	const organizationId = useJobillaOrgId()

	const assetsQuery = useMediaImageIndex()

	const assets = useMemo<MediaAsset[]>(() => {
		return assetsQuery.data?.map(mapImageDataToMediaAsset) ?? []
	}, [assetsQuery.data])

	// Kill switch for the signed-upload flow. Defaults to off so we ship with the
	// legacy multipart path active; flip the PostHog flag to roll the new path out
	// (gradual % rollout supported) and flip it back to revert instantly without a
	// deploy. Remove the flag (and the legacy branch below) once we're confident.
	const signedUploadsEnabled = useBooleanFlagValue('asset-hub-signed-uploads', false)

	// Both hooks are called unconditionally to satisfy the rules of hooks. Only one
	// is exercised per render — the other sits idle until/unless the flag flips.
	const signedMutation = useSignedImageUpload({ mutationKey: ['media-image-upload'] })
	const legacyMutation = useMediaImageUpload({ mutationKey: ['media-image-upload'] })

	// AssetGrid calls mutateAsync({ organisation, data, onProgress }). The legacy
	// multipart mutation doesn't know about onProgress; we drop it on the floor for
	// that branch. The response shape is structurally identical between the two
	// (id, name, file_name, created_at, ...), so the consumer doesn't care which fired.
	// Invalidation is intentionally NOT wired here — the AssetGrid runs uploads in
	// batches and invalidates once per batch so the grid swaps placeholders for real
	// cards in a single render rather than one-by-one.
	const uploadMutation = {
		mutateAsync: async (vars: SignedImageUploadVariables) => {
			if (signedUploadsEnabled) {
				return signedMutation.mutateAsync(vars)
			}
			// The legacy multipart hook knows nothing about onProgress or AbortSignal;
			// drop both before forwarding. Cancellation isn't supported on the legacy path.
			const { onProgress: _ignoredProgress, signal: _ignoredSignal, ...legacyVars } = vars
			return legacyMutation.mutateAsync(legacyVars)
		},
	}

	return {
		assets,
		isLoading: assetsQuery.isLoading,
		organizationId,
		uploadMutation,
	}
}
